import { Theme } from '@mui/material/styles'

/**
 * Builds the `sx` objects for a field settings card.
 *
 * Card borders and margins depend on how a card combines with its neighbours,
 * so `getPaperSx` takes the combine flags at call time rather than baking them
 * in. The only theme-driven difference is the left border, which is omitted
 * under the Tidal theme and kept otherwise; that is resolved here once per
 * render.
 *
 * `getRowSx` likewise takes `hideRequired` at call time, because the legacy
 * theme drops the row's bottom padding when the required toggle is hidden.
 *
 * `props` holds the theme-varying values that are not `sx`, because they select
 * a component prop or theme-specific copy rather than a CSS value.
 *
 * @param theme The active MUI theme.
 * @param isTidalThemeActive Whether the Tidal theme is active.
 */
export const getStyles = (theme: Theme, isTidalThemeActive: boolean) => ({
  getPaperSx: (isTopCombine: boolean, isBottomCombine: boolean) => ({
    borderColor: '#D3D7DC',
    borderTopColor: '#D3D7DC !important',
    borderBottomColor: '#D3D7DC !important',
    borderBottom: isTopCombine ? 0 : 1,
    borderTop: isBottomCombine ? 0 : 1,
    marginBottom: isTopCombine ? undefined : 1.5,
    borderEndEndRadius: isTopCombine ? 0 : undefined,
    borderEndStartRadius: isTopCombine ? 0 : undefined,
    borderStartEndRadius: isBottomCombine ? 0 : undefined,
    borderStartStartRadius: isBottomCombine ? 0 : undefined,
    ...(isTidalThemeActive ? { borderLeft: 0 } : {}),
  }),

  getRowSx: (hideRequired: boolean) =>
    isTidalThemeActive
      ? {
          p: 2,
          alignContent: 'center',
          justifyContent: 'space-between',
          alignItems: 'center',
        }
      : {
          p: 2,
          paddingBottom: hideRequired ? 0 : 2,
          alignItems: 'center',
        },

  checkboxWrapper: isTidalThemeActive ? { pr: 2 } : {},

  manageFunnelButton: {
    maxHeight: '36px',
    textTransform: isTidalThemeActive ? ('none' as const) : undefined,
  },

  manageFunnelIcon: { marginLeft: theme.spacing(1), fontSize: '20px' },

  props: {
    checkboxEdge: isTidalThemeActive ? undefined : ('start' as const),
    manageFunnelLabel: isTidalThemeActive
      ? 'Manage sales funnel'
      : 'Manage Sales Funnel',
  },
})

/**
 * Builds the `sx` objects for a card's display-option radio group.
 *
 * `getInnerSx` takes the caller's explicit `marginLeft` because the option
 * list is indented differently depending on which field renders it; when the
 * caller gives no value the theme's own default indent applies.
 *
 * @param _theme The active MUI theme.
 * @param isTidalThemeActive Whether the Tidal theme is active.
 */
export const getOptionsStyles = (
  _theme: Theme,
  isTidalThemeActive: boolean,
) => ({
  outerRow: { px: 2, pt: 0, pb: 0, pl: isTidalThemeActive ? 0 : 2 },
  radioRow: {
    pl: isTidalThemeActive ? 2 : '8px',
    pt: 0,
    pb: '10px',
    pr: '10px',
  },

  getInnerSx: (marginLeft?: number) => ({
    ml: marginLeft !== undefined ? marginLeft : isTidalThemeActive ? 7.75 : 5.4,
  }),

  examplesRow: { px: isTidalThemeActive ? 9.75 : 7 },
})

/**
 * Builds the `sx` objects for a card's label and toggle rows.
 *
 * One set of styles covers both themes: the Tidal container is a flex column,
 * the legacy one a plain block with outer margins, and the label and
 * "required" text take an explicit colour only under the legacy theme. Keeping
 * these here is what lets `FieldSettingsCardContent` render a single tree.
 *
 * @param theme The active MUI theme.
 * @param isTidalThemeActive Whether the Tidal theme is active.
 */
export const getContentStyles = (
  theme: Theme,
  isTidalThemeActive: boolean,
) => ({
  container: isTidalThemeActive
    ? { display: 'flex', flexDirection: 'column' as const }
    : { ml: 2, mr: 'auto' },

  label: {
    mb: 1,
    color: isTidalThemeActive ? undefined : theme.palette.text.primary,
  },

  requiredText: {
    letterSpacing: 0.15,
    color: isTidalThemeActive ? undefined : theme.palette.text.primary,
  },

  props: {
    labelVariant: isTidalThemeActive ? ('h6' as const) : ('subtitle1' as const),
  },
})
